import React, { useEffect, useState } from 'react';
import { PassiveSkillMilestone } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';

interface PassiveSkillMilestoneModalProps {
  isOpen: boolean;
  milestone: PassiveSkillMilestone | null;
  skillName: string;
  onClaim: () => void;
}

const PassiveSkillMilestoneModal: React.FC<PassiveSkillMilestoneModalProps> = ({
  isOpen,
  milestone,
  skillName,
  onClaim
}) => {
  const { t } = useLanguage();
  const [showFlash, setShowFlash] = useState(false);
  const [showContent, setShowContent] = useState(false);
  
  // Get demo mode from context
  const { isDemoMode } = useUser();
  
  // Track if milestone modal has been shown in demo mode
  const [hasShownDemoMilestone, setHasShownDemoMilestone] = useState(() => {
    return sessionStorage.getItem('demo_milestone_shown') === 'true';
  });

  useEffect(() => {
    if (isOpen) {
      // In demo mode, only show once
      if (isDemoMode && hasShownDemoMilestone) {
        // Skip showing modal in demo mode if already shown
        onClaim();
        return;
      }
      
      // Flash effect
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 300);
      
      // Show content after flash
      setTimeout(() => setShowContent(true), 350);
      
      // Mark as shown in demo mode
      if (isDemoMode && !hasShownDemoMilestone) {
        setHasShownDemoMilestone(true);
        sessionStorage.setItem('demo_milestone_shown', 'true');
      }
    } else {
      setShowContent(false);
    }
  }, [isOpen, isDemoMode, hasShownDemoMilestone, onClaim]);

  if (!isOpen || !milestone) return null;

  const getMilestoneIcon = (type: string) => {
    switch (type) {
      case 'streak':
        return '🔥';
      case 'level':
        return '⬆️';
      case 'total':
        return '🏅';
      default:
        return '⭐';
    }
  };

  const getMilestoneTitle = (type: string, value: number) => {
    switch (type) {
      case 'streak':
        return t.passiveSkills.dayStreak.replace('{value}', value.toString());
      case 'level':
        return t.passiveSkills.levelReached.replace('{value}', value.toString());
      case 'total':
        return t.passiveSkills.completions.replace('{value}', value.toString());
      default:
        return t.passiveSkills.milestoneReached;
    }
  };

  const getMilestoneColor = (type: string) => {
    switch (type) {
      case 'streak':
        return { 
          gradient: 'from-orange-950/50 via-orange-900/50 to-orange-950/50',
          border: 'border-orange-500/30',
          text: 'text-orange-400',
          glow: 'rgba(249, 115, 22, 0.4)'
        };
      case 'level':
        return { 
          gradient: 'from-blue-950/50 via-blue-900/50 to-blue-950/50',
          border: 'border-blue-500/30',
          text: 'text-blue-400',
          glow: 'rgba(59, 130, 246, 0.4)'
        };
      case 'total':
        return { 
          gradient: 'from-purple-950/50 via-purple-900/50 to-purple-950/50',
          border: 'border-purple-500/30',
          text: 'text-purple-400',
          glow: 'rgba(168, 85, 247, 0.4)'
        };
      default:
        return { 
          gradient: 'from-green-950/50 via-green-900/50 to-green-950/50',
          border: 'border-green-500/30',
          text: 'text-green-400',
          glow: 'rgba(34, 197, 94, 0.4)'
        };
    }
  };

  const colors = getMilestoneColor(milestone.type);

  return (
    <>
      {/* Flash Effect */}
      {showFlash && (
        <div className={`fixed inset-0 bg-${milestone.type === 'streak' ? 'orange' : milestone.type === 'level' ? 'blue' : 'purple'}-400/30 z-[9999] animate-fade-out pointer-events-none`} />
      )}

      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9998] p-2 sm:p-4">
        {showContent && (
          <div className="relative max-w-sm sm:max-w-md w-full animate-scale-in max-h-[85vh] overflow-y-auto">
            {/* Main Card */}
            <div className={`bg-gradient-to-br ${colors.gradient} border-2 ${colors.border} rounded-lg p-3 sm:p-6 backdrop-blur-sm shadow-[0_0_30px_${colors.glow}]`}>
              {/* Glow Effect */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-transparent via-white/10 to-transparent blur-3xl animate-pulse pointer-events-none" />

              {/* Content */}
              <div className="relative z-10">
                {/* Header */}
                <div className="text-center mb-3 sm:mb-4">
                  <div className="text-4xl sm:text-6xl mb-2 sm:mb-3 animate-bounce">
                    {getMilestoneIcon(milestone.type)}
                  </div>
                  <h2 className={`text-lg sm:text-2xl font-orbitron font-black ${colors.text} uppercase mb-1 glow-text`}>
                    {t.passiveSkills.milestoneReached}
                  </h2>
                  <p className="text-base sm:text-lg font-orbitron font-bold text-white">
                    {getMilestoneTitle(milestone.type, milestone.value)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400 font-rajdhani mt-1">
                    {skillName}
                  </p>
                </div>

                {/* Rewards Section */}
                <div className="bg-black/40 rounded-lg p-2 sm:p-4 mb-3 sm:mb-4 border border-white/10">
                  <h3 className="text-sm sm:text-base font-orbitron font-bold text-white uppercase text-center mb-2 sm:mb-3">
                    {t.passiveSkills.rewards}
                  </h3>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    {milestone.reward.xp && (
                      <div className="bg-blue-950/50 border border-blue-500/30 rounded-lg p-2 sm:p-3 text-center">
                        <div className="text-xl sm:text-2xl mb-1">⚡</div>
                        <div className="text-blue-400 font-orbitron font-bold text-lg sm:text-xl">
                          +{milestone.reward.xp}
                        </div>
                        <div className="text-gray-400 font-rajdhani text-xs uppercase">{t.passiveSkills.xp}</div>
                      </div>
                    )}
                    
                    {milestone.reward.currency && (
                      <div className="bg-yellow-950/50 border border-yellow-500/30 rounded-lg p-2 sm:p-3 text-center">
                        <div className="text-xl sm:text-2xl mb-1">💰</div>
                        <div className="text-yellow-400 font-orbitron font-bold text-lg sm:text-xl">
                          +{milestone.reward.currency}
                        </div>
                        <div className="text-gray-400 font-rajdhani text-xs uppercase">{t.passiveSkills.gold}</div>
                      </div>
                    )}
                    
                    {milestone.reward.statPoints && (
                      <div className="bg-green-950/50 border border-green-500/30 rounded-lg p-2 sm:p-3 text-center col-span-2">
                        <div className="text-xl sm:text-2xl mb-1">✨</div>
                        <div className="text-green-400 font-orbitron font-bold text-lg sm:text-xl">
                          +{milestone.reward.statPoints}
                        </div>
                        <div className="text-gray-400 font-rajdhani text-xs uppercase">{t.passiveSkills.statPoints}</div>
                      </div>
                    )}
                  </div>

                  {/* Title Reward */}
                  {milestone.reward.title && (
                    <div className="mt-2 sm:mt-3 bg-gradient-to-r from-purple-950/50 via-pink-900/50 to-purple-950/50 border-2 border-purple-500/50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="text-lg sm:text-xl mb-1">👑</div>
                      <div className="text-purple-400 font-orbitron font-bold text-sm sm:text-base uppercase">
                        {t.passiveSkills.newTitle}
                      </div>
                      <div className="text-white font-rajdhani text-sm sm:text-base mt-0.5">
                        "{milestone.reward.title}"
                      </div>
                    </div>
                  )}
                </div>

                {/* Motivational Message */}
                <div className="bg-black/40 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4 border border-white/10">
                  <p className="text-center font-rajdhani text-gray-300 text-xs italic">
                    "{getMilestoneMessage(t, milestone.type, milestone.value)}"
                  </p>
                  <p className="text-center text-[10px] text-gray-500 mt-1 font-rajdhani">{t.passiveSkills.theSystem}</p>
                </div>

                {/* Claim Button */}
                <button
                  onClick={onClaim}
                  className={`w-full bg-gradient-to-r ${colors.gradient.replace('950/50', '600')} hover:${colors.gradient.replace('950/50', '500')} text-white font-orbitron font-bold py-2 sm:py-3 px-3 sm:px-4 rounded uppercase text-xs sm:text-sm tracking-wider transition-all shadow-[0_0_15px_${colors.glow}] hover:shadow-[0_0_25px_${colors.glow}] border-2 ${colors.border}`}
                >
                  {t.passiveSkills.claimRewards}
                </button>
              </div>
            </div>

            {/* Outer Glow */}
            <div className={`absolute inset-0 border-2 ${colors.border} rounded-lg animate-pulse pointer-events-none`} style={{ 
              boxShadow: `0 0 30px ${colors.glow}` 
            }} />
          </div>
        )}
      </div>
    </>
  );
};

// Helper function for motivational messages
const getMilestoneMessage = (t: any, type: string, value: number): string => {
  const messages = {
    streak: t.passiveSkills.streakMessages,
    level: t.passiveSkills.levelMessages,
    total: t.passiveSkills.totalMessages
  };

  const typeMessages = messages[type as keyof typeof messages] || messages.streak;
  return typeMessages[Math.floor(Math.random() * typeMessages.length)];
};

export default PassiveSkillMilestoneModal;
